import { useEffect, useState } from "react";
import axios from "axios";
import { Box, Flex, Grid, Heading, Text } from "@chakra-ui/react";
import {
  FaBuilding,
  FaEarthAfrica,
  FaEnvelope,
  FaMessage,
  FaPhone,
  FaShieldHalved,
  FaStore,
  FaUser,
} from "react-icons/fa6";

import {
  FloatInput,
  SubmitBtn,
  Toast,
  isValidEmail,
} from "../../../login/components/SigninAtoms";
import Select from "../atoms/Select";
import Textarea from "../atoms/Textarea";
import MultiSelect from "../atoms/MultiSelect";

const ACCENT = "#04aec1";

const RETAILER_OPTIONS = [
  "Pick n Pay",
  "Checkers",
  "Dis-Chem",
  "Clicks",
  "Makro",
  "Spar",
  "Woolworths",
  "Game",
];

const REGION_OPTIONS = [
  "South Africa",
  "Botswana",
  "Namibia",
  "Zambia",
  "Kenya",
  "United Kingdom",
  "United Arab Emirates",
  "Other",
];

const isValidCell = (c) => c.length === 10 && /^\d+$/.test(c);

const ContactForm = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [cellnr, setCellnr] = useState("");
  const [company, setCompany] = useState("");
  const [region, setRegion] = useState("");
  const [retailers, setRetailers] = useState([]);
  const [message, setMessage] = useState("");
  const [state, setState] = useState("idle");
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const submit = async (e) => {
    e.preventDefault();
    if (state !== "idle") return;

    if (!name.trim() || !message.trim() || (!email && !cellnr)) {
      setToast({
        status: "error",
        title: "Missing details",
        description:
          "Add your name, a message, and either an email or phone number.",
      });
      return;
    }
    if (email && !isValidEmail(email)) {
      setToast({
        status: "error",
        title: "Invalid email",
        description: "Please enter a valid email address.",
      });
      return;
    }
    if (cellnr && !isValidCell(cellnr)) {
      setToast({
        status: "error",
        title: "Invalid phone",
        description: "Please enter a 10-digit phone number.",
      });
      return;
    }

    setState("loading");
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/marketing/contact-us`,
        { name, email, cellnr, company, region, retailers, message },
      );
      const { status, title, description } = res.data || {};
      setState("success");
      setToast({
        status: status || "success",
        title: title || "We'll be in touch",
        description: description || "We respond within 1 business day.",
      });
      setTimeout(() => {
        setState("idle");
        setName("");
        setEmail("");
        setCellnr("");
        setCompany("");
        setRegion("");
        setRetailers([]);
        setMessage("");
      }, 1800);
    } catch (err) {
      setState("idle");
      setToast({
        status: "error",
        title: "Something went wrong",
        description: "Please try again in a moment.",
      });
    }
  };

  return (
    <Box
      as="form"
      onSubmit={submit}
      position="relative"
      bg="rgba(255,255,255,0.85)"
      border="1px solid rgba(15,22,38,0.08)"
      borderRadius="24px"
      padding={{ base: "24px", md: "32px" }}
      backdropFilter="blur(20px) saturate(160%)"
      boxShadow="0 40px 80px -40px rgba(15,22,38,0.18), 0 12px 30px -16px rgba(15,22,38,0.10)"
      display="flex"
      flexDirection="column"
      gap="14px"
    >
      <Toast toast={toast} dark={false} />

      <Box>
        <Heading
          as="h2"
          m={0}
          fontSize="22px"
          fontWeight={700}
          letterSpacing="-0.02em"
          color="#0a0e1a"
        >
          Send us a note
        </Heading>
        <Text
          mt="6px"
          mb={0}
          fontSize="13px"
          color="rgba(15,22,38,0.6)"
        >
          Tell us what you sell, where you sell it, and what we can help with.
        </Text>
      </Box>

      <Grid templateColumns={{ base: "1fr", sm: "1fr 1fr" }} gap="12px">
        <FloatInput
          label="Full name"
          value={name}
          onChange={setName}
          icon={<FaUser />}
          accent={ACCENT}
          dark={false}
          autoComplete="name"
        />
        <FloatInput
          label="Work email"
          value={email}
          onChange={setEmail}
          type="email"
          icon={<FaEnvelope />}
          accent={ACCENT}
          dark={false}
          autoComplete="email"
          valid={email.length > 0 && isValidEmail(email)}
        />
      </Grid>

      <Grid templateColumns={{ base: "1fr", sm: "1fr 1fr" }} gap="12px">
        <FloatInput
          label="Phone / WhatsApp"
          value={cellnr}
          onChange={(v) => setCellnr(v.replace(/\D/g, "").slice(0, 10))}
          type="tel"
          icon={<FaPhone />}
          accent={ACCENT}
          dark={false}
          autoComplete="tel"
        />
        <FloatInput
          label="Company"
          value={company}
          onChange={setCompany}
          icon={<FaBuilding />}
          accent={ACCENT}
          dark={false}
          autoComplete="organization"
        />
      </Grid>

      <Select
        label="Country / region"
        value={region}
        onChange={setRegion}
        options={REGION_OPTIONS}
        icon={<FaEarthAfrica />}
        accent={ACCENT}
      />

      <MultiSelect
        label="Retailer feeds"
        value={retailers}
        onChange={setRetailers}
        options={RETAILER_OPTIONS}
        icon={<FaStore />}
        accent={ACCENT}
      />

      <Textarea
        label="Message"
        value={message}
        onChange={setMessage}
        icon={<FaMessage />}
        rows={5}
        accent={ACCENT}
      />

      <Box mt="4px">
        <SubmitBtn
          state={state}
          label="Send message"
          loadingLabel="Sending…"
          successLabel="Message sent"
          accent={ACCENT}
        />
      </Box>

      <Flex
        align="center"
        gap="10px"
        fontSize="11px"
        color="rgba(15,22,38,0.55)"
        mt="2px"
      >
        <Box color={ACCENT} fontSize="12px" display="grid" placeItems="center">
          <FaShieldHalved />
        </Box>
        We respond within{" "}
        <Box as="strong" color="#0a0e1a">
          1 business day
        </Box>
        . Your details stay private.
      </Flex>
    </Box>
  );
};

export default ContactForm;
